import { Radio } from 'antd';
import Button from 'components/Button/Button';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import Calendar from 'components/TrackingComponents/Calendar/Calendar';
import dayjs from 'dayjs';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { OrderStatus } from 'types/schema';
import media from 'utils/mediaStyle';
import CSV_Export from './CSV_Export.svg';
// import FilterWrapper from './FilterWrapper';
import utilMessages from 'utils/messages';
import messages from './messages';

const Label = styled.label`
  color: rgba(0, 0, 0, 0.54);
  font-size: 14px;
  margin-bottom: 5px;
`;

const FilterItem = styled.div`
  margin-bottom: 6pt;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const FilterAction = styled.div`
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;

  ${media.md`
    flex-direction: row;
    ${FilterItem} + ${FilterItem} {
      padding-left: 8px;
    }
  `};
`;
const Icon = styled(SVGIcon)`
  width: 18px;
  height: 18px;
  margin: auto;
`;

const ExportButton = styled(Button)`
  min-width: 120px;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
`;

const TodayButton = styled(Button)<{ active?: boolean }>`
  ${({ active }) =>
    active
      ? `
    background-color: #f0f9f0;
    border-color: #43a047;
    color: #43a047;
  `
      : ''}
  @media (max-width: 767px) {
    font-size: 14px;
  }
`;
const ClearFilterButton = styled.div`
  color: #1e7afd;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  padding: 4px 8px;
  user-select: none;
  display: inline-block;
  margin-top: 6px;
`;

interface FilterProps {
  isPenddingApproval?: boolean;
  [key: string]: any;
}

export default class Filter extends React.Component<FilterProps> {
  public state = {
    statuses: this.props.statuses,
    supplierName: this.props.supplierName,
    orderDateStart: this.props.orderDateStart && dayjs(this.props.orderDateStart),
    orderDateEnd: this.props.orderDateEnd && dayjs(this.props.orderDateEnd),
    deliveryDateStart: this.props.deliveryDateStart && dayjs(this.props.deliveryDateStart),
    deliveryDateEnd: this.props.deliveryDateEnd && dayjs(this.props.deliveryDateEnd),
    selectedDate: this.props.deliveryDateStart ? 'deliveryDate' : 'orderDate',
  };

  public componentWillReceiveProps(nextProps: any) {
    this.setState({
      statuses: nextProps.statuses,
      supplierName: nextProps.supplierName,
      orderDateStart: nextProps.orderDateStart && dayjs(nextProps.orderDateStart),
      orderDateEnd: nextProps.orderDateEnd && dayjs(nextProps.orderDateEnd),
      deliveryDateStart: nextProps.deliveryDateStart && dayjs(nextProps.deliveryDateStart),
      deliveryDateEnd: nextProps.deliveryDateEnd && dayjs(nextProps.deliveryDateEnd),
      selectedDate: nextProps.deliveryDateStart ? 'deliveryDate' : 'orderDate',
    });
  }

  public computeFilter = () => {
    const { statuses, supplierName, selectedDate, orderDateStart, orderDateEnd, deliveryDateStart, deliveryDateEnd } =
      this.state;

    const dateRange =
      selectedDate === 'orderDate'
        ? orderDateStart &&
          orderDateEnd && {
            orderDateRange: {
              start: orderDateStart.startOf('day').toDate(),
              end: orderDateEnd.endOf('day').toDate(),
            },
          }
        : deliveryDateStart &&
          deliveryDateEnd && {
            deliveryDateRange: {
              start: deliveryDateStart.startOf('day').toDate(),
              end: deliveryDateEnd.endOf('day').toDate(),
            },
          };

    return {
      statuses: statuses,
      supplierName: supplierName,
      ...(dateRange || {}),
    };
  };

  public onApplyFilter = () => {
    this.props.onApplyFilter({
      // pagination: {
      //   page: 0,
      // },
      filter: this.computeFilter(),
    });
  };

  public filterPendingApproval = () => {
    const isPendingApprovalActive =
      !Array.isArray(this.props.statuses) && this.props.statuses === OrderStatus.PendingApproval;

    if (isPendingApprovalActive) {
      this.props.onApplyFilter({
        filter: {
          ...this.computeFilter(),
          statuses: [OrderStatus.Sent, OrderStatus.Accepted, OrderStatus.InProgress, OrderStatus.PendingApproval],
        },
      });
    } else {
      this.props.onApplyFilter({
        filter: {
          ...this.computeFilter(),
          statuses: [OrderStatus.PendingApproval],
        },
      });
    }
  };

  public onResetToTab = () => {
    this.setState({
      selectedDate: 'orderDate',
      orderDateStart: undefined,
      orderDateEnd: undefined,
      deliveryDateStart: undefined,
      deliveryDateEnd: undefined,
    });

    this.props.onApplyFilter({
      // pagination: {
      //   page: 0,
      // },
      filter: {
        ...this.computeFilter(),
        statuses: [OrderStatus.Sent, OrderStatus.Accepted, OrderStatus.InProgress, OrderStatus.PendingApproval],
        orderDateRange: undefined,
        deliveryDateRange: undefined,
      },
    });
  };

  public onClearFilter = () => {
    this.setState({
      statuses: undefined,
      supplierName: undefined,
    });

    this.props.onClearFilter();
  };

  public onExportCSV = () => {
    this.props.onExportCSV(this.computeFilter());
  };

  public onChangeFilter = (key: string) => (value: string) => {
    this.setState({
      [key]: value,
    });
  };

  public onChangeDeliveryDate = ([start, end]) => {
    this.setState(
      {
        deliveryDateStart: start,
        deliveryDateEnd: end,
      },
      () => {
        if (start && end) {
          this.onApplyFilter();
        }
      },
    );
  };

  public onClearDeliveryDate = () => {
    this.setState({ deliveryDateStart: null, deliveryDateEnd: null }, () => {
      this.props.onApplyFilter({
        filter: {
          ...this.computeFilter(),
          deliveryDateRange: undefined,
        },
      });
    });
  };

  public onChangeOrderDate = ([start, end]) => {
    this.setState(
      {
        orderDateStart: start,
        orderDateEnd: end,
      },
      () => {
        if (start && end) {
          this.onApplyFilter();
        }
      },
    );
  };

  public onClearOrderDate = () => {
    this.setState({ orderDateStart: null, orderDateEnd: null }, () => {
      this.props.onApplyFilter({
        filter: {
          ...this.computeFilter(),
          orderDateRange: undefined,
        },
      });
    });
  };

  public toggleSelectedDate = (event) => {
    this.setState({
      selectedDate: event.target.value,
    });
  };

  public isTodaySelected = () => {
    const { selectedDate, orderDateStart, orderDateEnd, deliveryDateStart, deliveryDateEnd } = this.state;
    const today = dayjs();

    if (selectedDate === 'orderDate') {
      return (
        orderDateStart &&
        orderDateEnd &&
        orderDateStart.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') &&
        orderDateEnd.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
      );
    }
    return (
      deliveryDateStart &&
      deliveryDateEnd &&
      deliveryDateStart.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') &&
      deliveryDateEnd.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
    );
  };

  public setToday = () => {
    const today = dayjs();
    const { selectedDate, orderDateStart, orderDateEnd, deliveryDateStart, deliveryDateEnd } = this.state;

    // Kiểm tra xem button có đang active không (đã chọn today)
    const isTodayActive =
      selectedDate === 'orderDate'
        ? orderDateStart &&
          orderDateEnd &&
          orderDateStart.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') &&
          orderDateEnd.format('YYYY-MM-DD') === today.format('YYYY-MM-DD')
        : deliveryDateStart &&
          deliveryDateEnd &&
          deliveryDateStart.format('YYYY-MM-DD') === today.format('YYYY-MM-DD') &&
          deliveryDateEnd.format('YYYY-MM-DD') === today.format('YYYY-MM-DD');

    // Nếu đang active thì clear, nếu không thì set today
    if (isTodayActive) {
      if (selectedDate === 'orderDate') {
        this.setState(
          {
            orderDateStart: null,
            orderDateEnd: null,
          },
          () => {
            this.onApplyFilter();
          },
        );
      } else {
        this.setState(
          {
            deliveryDateStart: null,
            deliveryDateEnd: null,
          },
          () => {
            this.onApplyFilter();
          },
        );
      }
    } else {
      if (selectedDate === 'orderDate') {
        this.setState(
          {
            orderDateStart: today,
            orderDateEnd: today,
          },
          () => {
            this.onApplyFilter();
          },
        );
      } else {
        this.setState(
          {
            deliveryDateStart: today,
            deliveryDateEnd: today,
          },
          () => {
            this.onApplyFilter();
          },
        );
      }
    }
  };

  public render() {
    const { selectedDate, orderDateStart, orderDateEnd, deliveryDateStart, deliveryDateEnd } = this.state;
    return (
      <FilterContainer>
        <FilterAction>
          <FilterItem>
            <Label>
              <Radio.Group
                name="dateSelect"
                value={selectedDate}
                onChange={this.toggleSelectedDate}
                style={{ display: 'flex', gap: '24px' }}
              >
                <Radio value="orderDate">
                  <FormattedMessage {...messages.orderDate} />
                </Radio>

                <Radio value="deliveryDate">
                  <FormattedMessage {...messages.deliveryDate} />
                </Radio>
              </Radio.Group>
            </Label>
            {selectedDate === 'orderDate' ? (
              <Calendar
                trackingCategory="Orders Page | DatePicker"
                trackingAction="Search Order Date"
                value={[orderDateStart, orderDateEnd]}
                range
                onChange={this.onChangeOrderDate}
                onClear={this.onClearOrderDate}
                allowClear
                size="large"
              />
            ) : (
              <Calendar
                trackingCategory="Orders Page | DatePicker"
                trackingAction="Search Delivery Date"
                value={[deliveryDateStart, deliveryDateEnd]}
                range
                onChange={this.onChangeDeliveryDate}
                onClear={this.onClearDeliveryDate}
                allowClear
                size="large"
              />
            )}
          </FilterItem>

          <FilterItem>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!this.props.statuses && (
                <TodayButton
                  type="default"
                  size="large"
                  radius="20px"
                  onClick={this.setToday}
                  active={this.isTodaySelected()}
                >
                  <FormattedMessage {...utilMessages.today} />
                </TodayButton>
              )}
              {!this.props.statuses && selectedDate === 'orderDate' && orderDateStart && orderDateEnd && (
                <ClearFilterButton onClick={this.onClearOrderDate}>
                  <FormattedMessage {...messages.clearFilter} />
                </ClearFilterButton>
              )}
              {!this.props.statuses && selectedDate === 'deliveryDate' && deliveryDateStart && deliveryDateEnd && (
                <ClearFilterButton onClick={this.onClearDeliveryDate}>
                  <FormattedMessage {...messages.clearFilter} />
                </ClearFilterButton>
              )}

              {Array.isArray(this.props.statuses) &&
                (this.props.statuses.includes(OrderStatus.Sent) ||
                  this.props.statuses.includes(OrderStatus.Accepted) ||
                  this.props.statuses.includes(OrderStatus.InProgress) ||
                  this.props.statuses.includes(OrderStatus.PendingApproval)) && (
                  <>
                    <TodayButton
                      type="default"
                      size="large"
                      radius="20px"
                      onClick={this.filterPendingApproval}
                      active={
                        Array.isArray(this.props.statuses) &&
                        this.props.statuses.length === 1 &&
                        this.props.statuses.includes(OrderStatus.PendingApproval)
                      }
                      style={{ marginRight: '10px' }}
                    >
                      <FormattedMessage {...messages.pendingApprovalOrders} />
                    </TodayButton>

                    {((this.state.orderDateStart && this.state.orderDateEnd) ||
                      (this.state.deliveryDateStart && this.state.deliveryDateEnd) ||
                      (Array.isArray(this.props.statuses) &&
                        this.props.statuses.length === 1 &&
                        this.props.statuses.includes(OrderStatus.PendingApproval))) && (
                      <ClearFilterButton onClick={this.onResetToTab}>
                        <FormattedMessage {...messages.clearFilter} />
                      </ClearFilterButton>
                    )}
                  </>
                )}

              {this.props.isPenddingApproval && this.props.statuses === OrderStatus.PendingApproval && (
                <>
                  <TodayButton
                    type="default"
                    size="large"
                    radius="20px"
                    onClick={this.filterPendingApproval}
                    active={true}
                    style={{ marginRight: '10px' }}
                  >
                    <FormattedMessage {...messages.pendingApprovalOrders} />
                  </TodayButton>

                  {((this.state.orderDateStart && this.state.orderDateEnd) ||
                    (this.state.deliveryDateStart && this.state.deliveryDateEnd) ||
                    this.props.statuses === OrderStatus.PendingApproval) && (
                    <ClearFilterButton onClick={this.onResetToTab}>
                      <FormattedMessage {...messages.clearFilter} />
                    </ClearFilterButton>
                  )}
                </>
              )}
            </div>
          </FilterItem>
        </FilterAction>

        <ExportButton type="primary" size="large" ghost className="hideOnSmall" onClick={this.onExportCSV}>
          <Icon src={CSV_Export} />
          <FormattedMessage {...messages.exportCSV} />
        </ExportButton>
      </FilterContainer>
    );
  }
}
